import React, { useEffect, useState } from "react";
import { PaymentMethod } from "types";
import { useAppDispatch } from "../redux/hooks";
import { clearCart } from "../redux/slices/cartSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAuthCookie } from "../utils/authCookies";

interface PaymentMethodsProps {
  orderId: string;
  totalAmount: number;
  supplierId: string;
  id: string;                // user id (agent or team member)
  ordertype: "salesOrder" | "installationOrder" | "removalOrder" | string;

  onSubmitOffline?: (method: { name: string; _id: string }) => void;
  
  /** Payment history to check if dynamic methods were already used */
  paymentInfo?: Array<{
    method: string;
    payment_status?: string;
    [key: string]: any;
  }>;
}

export default function PaymentMethods({
  orderId,
  totalAmount,
  supplierId,
  id,
  ordertype,
  onSubmitOffline,
  paymentInfo = [],
}: PaymentMethodsProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{ name: string; _id: string } | null>(null);
  const token = getAuthCookie("sessionToken") || "";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // Fetch payment methods for this supplier + user
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${supplierId}/by-user/${id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) throw new Error(`Failed to load payment methods (${res.status})`);
        const data = await res.json();
        const methods = Array.isArray(data) ? data : [];
        
        // Check if any dynamic payment method has been used
        const hasUsedAnyDynamicMethod = paymentInfo.some(payment => {
          const paymentMethod = payment.method.toLowerCase().replace(/\s+/g, "_");
          return paymentMethod !== "online" && paymentMethod !== "cheque" && paymentMethod !== "bank_transfer";
        });
        
        // Filter payment methods based on usage
        const filteredMethods = methods.filter(method => {
          const methodName = method.name.toLowerCase().replace(/\s+/g, "_");
          
          // Always show online, cheque, bank_transfer regardless of usage
          if (methodName === "online" || methodName === "cheque" || methodName === "bank_transfer") {
            return true;
          }
          
          // If any dynamic method has been used, hide all dynamic methods
          return !hasUsedAnyDynamicMethod;
        });
        
        setPaymentMethod(filteredMethods);
        // If exactly one method, preselect it
        if (filteredMethods.length === 1 && filteredMethods[0]?.name) {
          setSelectedPaymentMethod({ name: filteredMethods[0].name, _id: filteredMethods[0]._id });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    if (supplierId && id && token) fetchPaymentMethods();
  }, [id, supplierId, token, paymentInfo]);

  // Internal complete-order (used only when onSubmitOffline is not provided)
  const completeOrderInternal = async (method: { name: string; _id: string }) => {
    setLoading(true);
    try {
      // Find the selected payment method to check is_credit
      const selectedPaymentMethodData = paymentMethod.find(pm => pm._id === method._id);
      const isCreditPayment = selectedPaymentMethodData?.is_credit || false;

      // console.log("Selected payment method:", selectedPaymentMethodData);
      // console.log("Is credit payment:", isCreditPayment);

      // Credit Payment Logic:
      // - If is_credit: false → User pays immediately → amount_paid: total, amount_due: 0
      // - If is_credit: true → User pays later → amount_paid: 0, amount_due: total
      
      const amountPaid = isCreditPayment ? 0 : totalAmount;
      const amountDue = isCreditPayment ? totalAmount : 0;

      // console.log("Amount paid:", amountPaid);
      // console.log("Amount due:", amountDue);

      // Select API endpoint based on order type
      let apiUrl = "";
      let httpMethod: "PUT" | "POST" = "PUT";
      switch (ordertype) {
        case "salesOrder":
          apiUrl = `${process.env.REACT_APP_API_URL}/orders/${orderId}/payment`;
          httpMethod = "PUT";
          break;
        case "installationOrder":
          apiUrl = `${process.env.REACT_APP_API_URL}/installation_orders/${orderId}/payment`;
          httpMethod = "POST";
          break;
        case "removalOrder":
          apiUrl = `${process.env.REACT_APP_API_URL}/removal_orders/${orderId}/payment`;
          httpMethod = "POST";
          break;
        default:
          throw new Error("Invalid order type");
      }

      const response = await fetch(apiUrl, {
        method: httpMethod,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          payment_id: "",
          total_amount: Number(totalAmount) || 0,
          amount_paid: amountPaid,
          amount_due: amountDue,
          payment_date: new Date().toISOString(),
          method: method.name,
        }),
      });

      if (!response.ok) throw new Error("Payment failed.");

      toast.success("Payment submitted successfully!");
      dispatch(clearCart());
      localStorage.removeItem("cart_id");
      navigate(0);
    } catch (err) {
      toast.error(`Error: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePay = () => {
    if (!selectedPaymentMethod) return;
    if (onSubmitOffline) {
      // Delegate to parent panel/page (Agent/Realtor) to handle offline flow
      onSubmitOffline(selectedPaymentMethod);
    } else {
      // Fallback: do the internal API call
      void completeOrderInternal(selectedPaymentMethod);
    }
  };

  if (loading && paymentMethod.length === 0) {
    return <div className="text-muted">Loading payment methods…</div>;
  }
  if (error) {
    return <div className="text-danger">Failed to load payment methods: {error}</div>;
  }

  return (
    <>
      <div>
        {paymentMethod.map((method) => {
          const isSelected = selectedPaymentMethod?._id === method._id;
          return (
            <div className="mb-2" key={method._id}>
              {/* pill row look */}
              <div
                className={`method-row ${isSelected ? "active" : ""}`}
                onClick={() => setSelectedPaymentMethod({ name: method.name, _id: method._id })}
              >
                <div className="form-check m-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dyanamicMethod"
                    id={`paymentMethod_${method._id}`}
                    checked={isSelected}
                    onChange={() => setSelectedPaymentMethod({ name: method.name, _id: method._id })}
                  />
                  <label
                    className={`form-check-label ${isSelected ? "text-success fw-semibold" : ""
                      }`}
                    htmlFor={`paymentMethod_${method._id}`}
                  >
                    {method.name}
                  </label>
                </div>
              </div>

              {/* action under the selected method */}
              {isSelected && (
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton br-8"
                    disabled={loading}
                    onClick={handlePay}
                  >
                    {loading ? "Processing…" : `Pay With ${method.name}`}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {paymentMethod.length === 0 && (
          <div className="text-muted">No payment methods available.</div>
        )}
      </div>

      {/* If you don't already have these styles somewhere, keep this block. */}
      <style>{`
    .method-row{
      border:1px solid #e7e9ef; border-radius:12px;
      padding:12px 14px; background:#fff;
      cursor:pointer; transition:box-shadow .15s ease,border-color .15s ease;
    }
    .method-row.active{
      border-color:#0d6efd; box-shadow:0 0 0 3px rgba(13,110,253,.12);
    }
  `}</style>
    </>
  );
}





























// import React, { useEffect, useState, useRef, use } from "react";
// import { PaymentMethod } from "types";
// import { useAppDispatch } from "../redux/hooks";
// import { clearCart } from "../redux/slices/cartSlice";
// import { Link, useParams, useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";

// interface PaymentMethodsProps {
//     orderId: string;
//     totalAmount: number;
//     supplierId: string;
//     id: string;
//     ordertype: string;
// }

// export default function PaymentMethods({
//     orderId,
//     totalAmount,
//     supplierId,
//     id,
//     ordertype,
// }: PaymentMethodsProps) {
//     const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
//     const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
//     const token = getAuthCookie("sessionToken") || "";
//     const dispatch = useAppDispatch();
//     const [loading, setLoading] = useState(true);
//     const navigate = useNavigate();
//     const [error, setError] = useState<string>("");

//     useEffect(() => {
//         const fetchPaymentMethods = async () => {
//             // const id = order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id
//             try {
//                 const response = await fetch(
//                     `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${supplierId}/by-user/${id}`,
//                     {
//                         method: "GET",
//                         headers: {
//                             Authorization: `Bearer ${token}`,
//                             "Content-Type": "application/json",
//                         },
//                     }
//                 );
//                 const data = await response.json();
//                 console.log("PaymentMethod :", data);
//                 setPaymentMethod(data);
//             } catch (err) {
//                 setError(
//                     err instanceof Error ? err.message : "An unknown error occurred"
//                 );
//             } finally {
//                 setLoading(false);
//             }
//         };
//         fetchPaymentMethods();
//     }, [id, supplierId, token]);

//     const completeOrder = async (getmethod: string) => {
//         setLoading(true);
//         try {
//             // 🔀 Select API endpoint based on ordertype
//             let apiUrl = "";
//             let httpMethod: "PUT" | "POST" = "PUT";
//             switch (ordertype) {
//                 case "salesOrder":
//                     apiUrl = `${process.env.REACT_APP_API_URL}/orders/${orderId}/payment`;
//                     httpMethod = "PUT"; // ✅ salesOrder uses PUT
//                     break;
//                 case "installationOrder":
//                     apiUrl = `${process.env.REACT_APP_API_URL}/installation_orders/${orderId}/payment`;
//                      httpMethod = "POST"; // ✅ installationOrder uses POST
//                     break;
//                 case "removalOrder":
//                     apiUrl = `${process.env.REACT_APP_API_URL}/removal_orders/${orderId}/payment`;
//                     httpMethod = "POST"; // ✅ removalOrder uses POST
//                     break;
//                 default:
//                     throw new Error("Invalid order type");
//             }

//             const response = await fetch(apiUrl, {
//                  method: httpMethod,
//                 headers: {
//                     "Content-Type": "application/json",
//                     Authorization: `Bearer ${token}`,
//                 },
//                 body: JSON.stringify({
//                     payment_id: "",
//                     total_amount: totalAmount,
//                     amount_paid: 0,
//                     amount_due: totalAmount,
//                     payment_date: new Date().toISOString(),
//                     method: getmethod,
//                 }),
//             });

//             if (!response.ok) throw new Error("Payment failed.");

//             toast.success("Payment submitted successfully!");
//             dispatch(clearCart());
//             localStorage.removeItem("cart_id");
//             navigate(-1);
//         } catch (error) {
//             toast.error(`Error: ${(error as Error).message}`);
//         } finally {
//             setLoading(false);
//         }
//     };


//     // const completeOrder = async (getmethod: string) => {
//     //     // console.log("getmethod", getmethod);
//     //     setLoading(true);

//     //     try {

//     //         // 🔁 Default online payment API
//     //         const response = await fetch(
//     //             `${process.env.REACT_APP_API_URL}/installation_orders/${installationOrder?._id}/payment`,
//     //             {
//     //                 method: "PUT",
//     //                 headers: {
//     //                     "Content-Type": "application/json",
//     //                     Authorization: `Bearer ${token}`,
//     //                 },
//     //                 body: JSON.stringify({
//     //                     payment_id: "",
//     //                     total_amount: formik.values.total,
//     //                     amount_paid: 0,
//     //                     amount_due: formik.values.total,
//     //                     payment_date: new Date().toISOString(),
//     //                     method: getmethod,
//     //                 }),
//     //             }
//     //         );

//     //         if (!response.ok) {
//     //             throw new Error("Online payment failed.");
//     //         }


//     //         toast.success("Payment submitted successfully!");
//     //         navigate(-1);
//     //         localStorage.removeItem("cart_id");

//     //     } catch (error) {
//     //         const errMessage =
//     //             (error as Error).message || "An unknown error occurred";
//     //         console.error("Order Failed:", errMessage);
//     //         toast.error(`Error: ${errMessage}`);
//     //     } finally {
//     //         setLoading(false);
//     //     }
//     // };

//     return (
//         <>
//             <div className="">
//                 {paymentMethod?.map((method) => (
//                     <div className="form-check my-3" key={method._id}>
//                         <input
//                             className="form-check-input"
//                             type="radio"
//                             name="paymentMethod"
//                             id={`paymentMethod_${method._id}`}
//                             checked={selectedPaymentMethod === method.name}
//                             onChange={() => setSelectedPaymentMethod(method.name)}
//                         />
//                         <label
//                             className={`form-check-label ${selectedPaymentMethod === method.name ? "text-success fw-semibold" : ""
//                                 }`}
//                             htmlFor={`paymentMethod_${method._id}`}
//                         >
//                             {method.name}
//                         </label>
//                     </div>
//                 ))}
//             </div>

//             {/* ✅ Complete Order button appears only if a method is selected */}
//             {selectedPaymentMethod && (
//                 <div className="d-flex align-items-start gap-3 mt-4">
//                     <button
//                         type="button"
//                         className="btn btn-primary"
//                         onClick={() => completeOrder(selectedPaymentMethod)}
//                     >
//                         Pay With {selectedPaymentMethod}
//                     </button>
//                 </div>
//             )}
//         </>
//     )
// }
